/* eslint-disable import/no-default-export */
import {
  AudienceCheck,
  AudienceDefinitions,
  AudienceName,
  Test,
  Variation,
} from './types';

const md5 = require('blueimp-md5');

const BUCKETS = 1000;

export default class SelectedVariation {
  audienceDefinitions: AudienceDefinitions;
  constructor(audienceDefinitions: AudienceDefinitions) {
    this.audienceDefinitions = audienceDefinitions;
  }
  static getHashedId(id: string, salt: string) {
    let result;
    if (salt) {
      result = id + salt;
    }

    return parseInt(md5(result).slice(0, 8), 16);
  }

  static variationsAndControl({ defaultWeight, variations }: Test) {
    const control: Variation = {
      name: 'control',
      weight: defaultWeight,
    };

    return [control].concat(variations);
  }

  getExperimentAllocation(test: Test) {
    const allocation = test.experimentAllocation;
    if (typeof allocation === 'number') {
      return allocation;
    } else {
      const allocations = Object.entries(allocation).map(
        (value: [AudienceName, string]) => {
          const [currentAudience, currentAllocation] = value;
          if (
            (this.audienceDefinitions[currentAudience] as AudienceCheck)(
              test.audienceSettings,
            )
          ) {
            return Number(currentAllocation);
          } else {
            return 0;
          }
        },
      );

      return Math.max(...allocations);
    }
  }

  getWeightedVariations(test: Test) {
    let cumlWeight = 0;

    return SelectedVariation.variationsAndControl(test)
      .filter((variation) => variation.weight > 0)
      .reduce(function (arr: Variation[], variation: Variation) {
        variation.cumlWeight = variation.weight + cumlWeight;
        cumlWeight = variation.cumlWeight;

        return arr.concat(variation);
      }, []);
  }

  inExperiment(test: Test, identifier: string) {
    const inExperimentBucket =
      SelectedVariation.getHashedId(
        test.name + test.experimentVersion,
        `in${identifier}`,
      ) % BUCKETS;
    const allocation = this.getExperimentAllocation(test);
    if (allocation > 0) {
      return inExperimentBucket <= allocation * BUCKETS;
    }

    return false;
  }

  v1(test: Test, identifier: string) {
    const variations = this.getWeightedVariations(test);
    // In order to limit the % of traffic that sees the experiment, as well as bucket into a specific variation we're going
    // to create buckets from 0 - (sum of variation weights / percent of traffic that will see the experiment) * 100
    // the member will be assigned randomly into one of those buckets. if the bucket # is higher than (the # of buckets * traffic allocation percentage),
    // the member will not see the experiment at all. The remaining buckets will be divided based on the variation weights specified in the test configuration

    // The total number of buckets
    const dynamicBuckets = Math.floor(
      ((variations[variations.length - 1].cumlWeight || 0) /
        this.getExperimentAllocation(test)) *
        100,
    );
    // The bucket for this member
    const bucket =
      SelectedVariation.getHashedId(
        test.name + test.experimentVersion,
        identifier,
      ) % dynamicBuckets;

    return variations.find((variation: Variation) => {
      return (variation.cumlWeight || 0) * 100 >= bucket;
    });
  }

  v2(test: Test, identifier: string) {
    const weightedVariations = this.getWeightedVariations(test);

    if (this.inExperiment(test, identifier)) {
      const variationBucket =
        SelectedVariation.getHashedId(
          test.name + test.experimentVersion,
          `variation${identifier}`,
        ) % BUCKETS;
      const maxWeight =
        weightedVariations[weightedVariations.length - 1].cumlWeight || 0;

      return weightedVariations.find((variation: Variation) => {
        return (
          ((variation.cumlWeight || 0) / maxWeight) * BUCKETS >= variationBucket
        );
      });
    }

    return undefined;
  }
}
